import { httpClient } from './httpClient';

export interface FormTemplate {
  id: number;
  name: string;
  description?: string;
  schema:
    | {
        fields: FormField[];
        [key: string]: any;
      }
    | FormField[]
    | any; // Allow for different schema structures
  created_at: string;
  updated_at: string;
  version?: number;
  is_latest?: boolean;
  parent_template_id?: number;
}

export interface FormField {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
  placeholder?: string;
  defaultValue?: any;
  [key: string]: any;
}

export const formTemplateService = {
  /**
   * Get all form templates
   */
  getFormTemplates(): Promise<FormTemplate[]> {
    return httpClient.get('/api/form-templates');
  },

  /**
   * Get paginated form templates
   */
  getPaginatedFormTemplates(page: number, limit: number, search?: string): Promise<{ data: FormTemplate[], total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    return httpClient.get(`/api/form-templates?${params.toString()}`);
  },

  /**
   * Get a specific form template by ID
   * @param id Form template ID
   */
  async getFormTemplate(id: number): Promise<FormTemplate> {
    try {
      const response = await httpClient.get<FormTemplate>(`/api/form-templates/${id}`);
      
      // Check if the schema structure is as expected
      if (response && typeof response === 'object' && 'schema' in response) {
        
        // Handle different schema structures
        if (response.schema && typeof response.schema === 'object') {
          // Case 1: schema is an object with fields property
          if ('fields' in response.schema && Array.isArray(response.schema.fields)) {
            console.log('Fields found in schema object:', response.schema.fields.length);
          }
          // Case 2: schema is an array directly
          else if (Array.isArray(response.schema)) {
            // Convert to expected format
            const schemaArray = [...response.schema];
            response.schema = { fields: schemaArray };
          } 
          // Case 3: Other unexpected schema format
          else {
            console.warn('Unexpected schema format:', response.schema);
            // Try to extract fields if possible
            const schemaObj = response.schema as Record<string, any>;
            const possibleFields = Object.values(schemaObj).find((val) => Array.isArray(val));
            if (possibleFields) {
              console.log('Found possible fields array in schema');
              response.schema = { fields: possibleFields as any[] };
            } else {
              console.warn('Could not find fields array in schema, creating empty fields array');
              response.schema = { fields: [] };
            }
          }
        } else {
          console.warn('Schema is not an object or array');
          (response as FormTemplate).schema = { fields: [] };
        }
      } else {
        console.warn('No schema found in response or response is not an object');
        // Create a default schema structure
        if (response && typeof response === 'object') {
          (response as FormTemplate).schema = { fields: [] };
        }
      }

      return response as FormTemplate;
    } catch (error) {
      console.error('Error fetching form template:', error);
      throw error;
    }
  }
};
